import { APIService } from '@/lib/services/api.service';
import { PageResult } from '@/lib/api/types';
import { getRuntimeConfig } from '@/lib/config/runtime';
import { getClientSessionId } from '@/lib/session/client-session';
import {
  InAppNotification,
  MarkAllNotificationsAsReadResponse,
  NotificationCounters,
  NotificationPreference,
  NotificationStreamEvent,
} from '@/modules/notifications/types';

export class NotificationsService extends APIService {
  constructor() {
    super('/notifications');
  }

  async getFeed(
    page = 0,
    size = 10,
    unreadOnly = false,
  ): Promise<PageResult<InAppNotification> | void> {
    return await this.get(`?page=${page}&size=${size}&unreadOnly=${unreadOnly}`);
  }

  async getCounters(): Promise<NotificationCounters | void> {
    return await this.get('/counters');
  }

  async markRead(id: string): Promise<NotificationCounters | void> {
    return await this.post(`/${id}/read`);
  }

  async markAllRead(): Promise<MarkAllNotificationsAsReadResponse | void> {
    return await this.post('/read-all');
  }

  async getPreferences(): Promise<NotificationPreference[] | void> {
    return await this.get('/preferences');
  }

  async updatePreferences(
    preferences: NotificationPreference[],
  ): Promise<NotificationPreference[] | void> {
    return await this.put('/preferences', preferences);
  }

  connectWebSocket(
    token: string,
    handlers: {
      onEvent: (event: NotificationStreamEvent) => void;
      onError?: (error: unknown) => void;
    },
  ): () => void {
    const { onEvent, onError } = handlers;

    // In mock mode there is no WS server; avoid endless reconnect loops in frontend.
    if (getRuntimeConfig().VITE_API_MODE !== 'real') {
      return () => {};
    }

    const wsUrl = this.resolveWebSocketUrl(token);
    const socket = new WebSocket(wsUrl);

    socket.onmessage = event => {
      try {
        const parsed = JSON.parse(String(event.data)) as NotificationStreamEvent;
        onEvent(parsed);
      } catch {
        // Ignore malformed payload.
      }
    };

    socket.onerror = error => {
      onError?.(error);
    };

    socket.onclose = event => {
      if (event.code !== 1000) {
        onError?.(new Error('Notifications websocket closed unexpectedly'));
      }
    };

    return () => {
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close(1000, 'client disconnect');
      }
    };
  }

  private resolveWebSocketUrl(token: string) {
    const { VITE_API_BASE_URL } = getRuntimeConfig();
    if (!VITE_API_BASE_URL) {
      throw new Error('VITE_API_BASE_URL is required');
    }

    const base = VITE_API_BASE_URL.replace(/\/$/, '');
    const wsBase = base.replace(/^http:/i, 'ws:').replace(/^https:/i, 'wss:');
    const clientSessionId = getClientSessionId() ?? '';
    const query = new URLSearchParams({
      token,
      ...(clientSessionId ? { clientSessionId } : {}),
    }).toString();

    return `${wsBase}/notifications/ws?${query}`;
  }
}

export const notificationsService = new NotificationsService();
